import { HubConnection, HubConnectionBuilder, ILogger, LogLevel, HttpError } from '@microsoft/signalr';
import { showSuccess, showWarning } from '@/modules/notification/engine/notification.library';
import { useAuthenticationStore } from '@/store';
import { runtimeEnv } from '@/config/runtime-env';
import { Subject, Subscription } from "rxjs";
import { useEffect } from 'react';


/* Currently I prefer using it as singleton and always used before in other projects.
But in case of high load we can try to use it as a provider and inject it in the component where it is needed.
*/
type PaymentTransactionService = {
	paymentTransactionSubject: { next: (notification: any) => void }
};

export class SignalRService {
	private static instance: SignalRService;
	public hubConnection!: HubConnection;
	isInitialConnectionAttempt = true;
	private paymentTransactionService?: PaymentTransactionService;
	//connected state reflects if hub connection is active
	connected = false;

	constructor(paymentTransactionService?: PaymentTransactionService) {
		// Singleton pattern
		if (SignalRService.instance) {
			return SignalRService.instance;
		}
		SignalRService.instance = this;
		this.paymentTransactionService = paymentTransactionService;

		if (!this.isWebSocketSupported()) {
			console.warn('WebSocket is not supported in this browser');
			// Optionally notify user or fall back to other transport
		}
		// Initialize connection but don't start it
		this.initializeConnection();
		this.setupConnectionHandlers();

		//Subscribe to future auth state changes (based on accessToken presence)
		useAuthenticationStore.subscribe((state, prevState) => {
			const token = state.accessToken;
			const prevToken = prevState.accessToken;
			if (token === prevToken) return;

			this.isInitialConnectionAttempt = true;
			if (token) {
				this.startConnectionWithRetry([]);
			} else {
				this.connected = false;
				this.stopConnection();
			}
		});

		// Handle initial state (Zustand subscribe does not emit immediately)
		const initialToken = useAuthenticationStore.getState().accessToken;
		if (initialToken) {
			this.startConnectionWithRetry([]);
		}
	}

	private initializeConnection() {
		this.hubConnection = new HubConnectionBuilder()
			.withUrl(`${runtimeEnv.apiBaseUrl}/push-notifications`, {
				accessTokenFactory: () => {
					const token = useAuthenticationStore.getState().accessToken;
					return token || '';
				},
				withCredentials: false
			})
			//.withAutomaticReconnect()
			//.configureLogging(new MyLogger())
			.configureLogging(LogLevel.Warning)
			.build();
	}

	private isWebSocketSupported(): boolean {
		return typeof WebSocket !== 'undefined';
	}

	private setupConnectionHandlers() {
		// Subscribe to connection state changes
		this.hubConnection.onreconnecting((error) => {
			console.log('Signalr Connection lost, trying to reconnect...', error);
			// TODO: hide this message from users after we do some testing
			showWarning('Push notifications connection lost, trying to reconnect...');
		});
		this.hubConnection.onreconnected((connectionId) => {
			console.log('Signalr Reconnected');
			showSuccess('Reconnected to push notifications');
		});
		this.hubConnection.onclose(async (error) => {
			if (!this.connected) return;

			//this.cu.showWarn("SignalR connect closed unexpectedly");
			let progressMessages = ["Live updates connection interrupted unexpectedly. It may happen because of server restart or network reasons."];
			// We can show more intrusive message, but not sure about that right now.
			// let dlg = this.dialogService.open(BlockAndProgressDialogComponent, {
			// 	data: {
			// 		progressMessages: progressMessages
			// 	},
			// 	closable: false,
			// 	header: "Restoring connection...",
			// 	width: "400px",
			// 	height: "200px",
			// 	styleClass: "pos-marker"
			// });
			this.connected = false;
			await this.startConnectionWithRetry(progressMessages);
			//dlg.close();
			console.log("SignalR connecttion restored");
			// Add your user notification logic here for permanent disconnection, e.g.:
			// this.messageService.add({ severity: 'error', summary: 'Disconnected', detail: 'Connection permanently lost.' });
		});

		this.hubConnection.on("OrderChanged", (notification: any) => {
			console.log('Order changed');
			console.log(notification);

			// TODO:
			emitOrderStatus(notification as OrderChangedDto);
			//this.paymentTransactionService?.paymentTransactionSubject?.next(notification);
		});

	}

	/*
	1. I replaced the built-in reconnection implementation with my own to have more control.
	2. Here the console gets spammed by error:
	POST http://localhost:5015/push-notifications/negotiate?negotiateVersion=1 net::ERR_CONNECTION_REFUSED
	We cannot hide it (same with built-in reconnction implementation).
	Chatgpt: You generally cannot prevent the browser from showing a network ERR_CONNECTION_REFUSED message in the console, because that is the browser’s own error reporting. configureLogging(LogLevel.None) only affects SignalR’s internal logs, not the browser-level network errors.
	If hiding that will be really required, then we can try pre-test connection using ordinary http requests (which we can implement to not fail and report gracefully).
	*/
	async startConnectionWithRetry(msgs: string[]) {
		//msgs.push(moment().format("HH:mm:ss ") + "Trying to restore connection");
		while (true) {
			if (await this.startConnection()) break;
			await this.delay(4000);
			//msgs.push(moment().format("HH:mm:ss ") + "Trying to restore connection");
		}
	}

	async startConnection() {
		let res = true;
		if (!this.isInitialConnectionAttempt) {
			console.log('Reconnecting to push notifications...');
		}
		await this.hubConnection
			.start()
			.catch((response: HttpError) => {
				res = false;
			});

		if (!res) {
			this.isInitialConnectionAttempt = false;
			return res;
		} else {
			this.connected = true;
			if (!this.isInitialConnectionAttempt) {
				console.log('Reconnected to push notifications');
			}
			this.isInitialConnectionAttempt = false;
		}
		return res;
	}

	async stopConnection() {
		//if (this.hubConnection.state !== 'Disconnected') {
		await this.hubConnection.stop();
		//this.connectionState.next(false);
		//}
	}

	private async delay(ms: number) {
		return new Promise<void>((resolve) => setTimeout(resolve, ms));
	}

	static getInstance(): SignalRService {
		if (!SignalRService.instance) {
			SignalRService.instance = new SignalRService();
		}
		return SignalRService.instance;
	}

	// Public method to allow components to subscribe to SignalR events
	public on(eventName: string, callback: (...args: any[]) => void): void {
		this.hubConnection.on(eventName, callback);
	}

	// Public method to allow components to unsubscribe from SignalR events
	public off(eventName: string, callback: (...args: any[]) => void): void {
		this.hubConnection.off(eventName, callback);
	}
}

export enum X4bActivityStatus {
	/** Order is template for future orders created with repeat schedule */
	RepeatScheduled = 0,

	/** New */
	New = 1,

	/** Rider assigning */
	RiderAssigning = 2,

	/** Rider assigned */
	RiderAssigned = 3,

	/** Departed to pickup - Not using yet */
	DepartedToPickup = 4,

	/** Picked Up */
	PickedUp = 5,

	/** Arrived To Drop */
	ArrivedToDrop = 6,

	/** Completed */
	Completed = 7,

	/** Canceled */
	Canceled = 8,

	/** Rejected */
	Rejected = 9,

	/** Expired */
	Expired = 10,

	/** Arrived To Pickup */
	ArrivedToPickup = 11,

	/** This when rider accept new order from schedule booking */
	RiderAccept = 12,

	/** Райдер принял новый заказ Back to Back, если у него на данный момент есть активный заказ
	 * Rider accepted a new Back to Back order if he currently has an active order
	 */
	RiderAcceptB2B = 13
}
export interface OrderChangedDto {
	bookingId: string;
	status: X4bActivityStatus;
	//public OrderDriverDto? Driver { get; set; }
}



export const orderStatusBus = new Subject<OrderChangedDto>(); // one global instance

export function emitOrderStatus(event: OrderChangedDto) {
	orderStatusBus.next(event);
}

export function useOrderStatusBus(handler: (e: OrderChangedDto) => void) {
	useEffect(() => {
		const sub: Subscription = orderStatusBus.subscribe(handler);
		return () => sub.unsubscribe();
	}, [handler]);
}

